import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  PlusSquareOutlined,
} from "@ant-design/icons";
import {
  Button,
  Popover,
  Space,
  Table,
  TableColumnsType,
  TablePaginationConfig,
} from "antd";
import {
  ResponseEntryDetail,
  ResponseInputRegisterMovements,
} from "api/generated";
import useLoggedUser from "hooks/useLoggedUser";
import moment from "moment";
import { Link, RouteComponentProps } from "react-router-dom";
import { EVM_PERMISSION, getPagePermissions } from "security/permissions";
import PgElimination from "./pgElimination";
import { UsePaginationTableReturn } from "hooks/commons/usePaginationTable";
import { Dispatch, SetStateAction, useContext, useMemo } from "react";
import { UseTableSelectionReturn } from "hooks/commons/useTableSelection";
import { FilterValue, SorterResult } from "antd/es/table/interface";
import { COMPLEMENT_PARENT } from "routes/cfdiComplements/ComplementsScreen";
import DistributionPointSelected from "context/MyDistributionPoint";

interface ReceptionTableProps extends RouteComponentProps {
  selection: UseTableSelectionReturn;
  isLoading: boolean;
  reloadTable: () => void;
  pagination: UsePaginationTableReturn<ResponseEntryDetail>;
  setFilterCfdi: Dispatch<SetStateAction<boolean | undefined>>;
  deleteComplement: (record: ResponseEntryDetail) => void;
}

function ReceptionTable({
  selection,
  isLoading,
  reloadTable,
  pagination,
  setFilterCfdi,
  deleteComplement,
  location,
}: ReceptionTableProps) {
  const { permissions } = useLoggedUser();
  const pagePermissions = getPagePermissions(EVM_PERMISSION, permissions);
  const { distributionPoint } = useContext(DistributionPointSelected);

  const receptions = useMemo(() => pagination.items, [pagination.items]);

  const columns: TableColumnsType<ResponseEntryDetail> = [
    {
      title: "Tipo",
      dataIndex: "type"
    },
    {
      title: "Fecha de inicio",
      dataIndex: "inputRegisterReceptionDate",
      render: (date) => moment(date).format("YYYY/MM/DD HH:mm:ss"),
    },
    {
      title: "Fecha de fin",
      dataIndex: "inputRegisterEndReceptionDate",
      render: (date) => moment(date).format("YYYY/MM/DD HH:mm:ss"),
      sorter: true,
    },
    { title: "Equipo", dataIndex: "inputRegisterEquipmentCode" },
    { title: "Origen", dataIndex: "pgVehicle" },
    {
      title: "Número de registro",
      dataIndex: "inputRegisterFolio",
      sorter: true,
    },
    {
      title: "Volumen",
      dataIndex: "inputRegisterVolume",
      render: (inputRegisterVolume) =>
        Intl.NumberFormat("en", { notation: "standard" }).format(
          inputRegisterVolume
        ),
    },
    {
      title: "Masa",
      dataIndex: "inputRegisterMass",
      render: (inputRegisterMass) =>
        Intl.NumberFormat("en", { notation: "standard" }).format(
          inputRegisterMass
        ),
    },
    { title: "Almacén", dataIndex: "inputRegisterWarehouseKey" },
    { title: "Serie", dataIndex: "invoiceSerie" /* sorter: true  */ },
    { title: "Folio", dataIndex: "invoiceFolio" /* sorter: true */ },
    {
      title: "CFDI",
      dataIndex: "fiscalId",
      filters: [
        { text: "Facturados", value: true },
        { text: "Sin CFDI", value: false },
      ],
      render: (text) => (text ? text.toUpperCase() : ""),
    },
    {
      title: "PGs",
      align: "center",
      render: (_text, record) =>
        record.isEditAllowed && record.inputRegisterAssociatedPg !== null ? (
          <div style={{ display: "inline-block" }}>
            <Space.Compact>
              {pagePermissions.edit && (
                <Link
                  key="2"
                  to={{
                    pathname: `/receptions/components/pgRegistration/${record.inputRegisterAssociatedPg?.id}/${record.id}`,
                  }}
                >
                  <Popover content="Editar PG">
                    <Button shape="circle" icon={<EditOutlined />} />
                  </Popover>
                </Link>
              )}
              {pagePermissions.delete && (
                <PgElimination
                  inputRegisterAssociatedPg={record.inputRegisterAssociatedPg}
                  handleSubmit={() => {
                    reloadTable();
                  }}
                />
              )}
            </Space.Compact>
          </div>
        ) : record.isEditAllowed &&
          record.inputRegisterAssociatedPg === null ? (
          <div>
            {pagePermissions.add && (
              <Link
                key="1"
                to={{
                  pathname: `/receptions/components/pgRegistration/0/${record.id}`,
                }}
              >
                <Popover content="Agregar PG">
                  <Button type="primary" icon={<PlusOutlined />} />
                </Popover>
              </Link>
            )}
          </div>
        ) : record.isEditAllowed === false &&
          record.inputRegisterAssociatedPg ? (
          <div>
            {pagePermissions.read && (
              <Link
                key="3"
                to={{
                  pathname: `/receptions/components/pgRegistration/${record.inputRegisterAssociatedPg.id}/${record.id}`,
                }}
              >
                <Popover content="Ver PG">
                  <Button icon={<EyeOutlined />} />
                </Popover>
              </Link>
            )}
          </div>
        ) : (
          <div>{pagePermissions.read && <span>Sin PG asociado</span>}</div>
        ),
    },
    {
      title: "Complemento",
      dataIndex: "complement",
      render: (_, record) => {
        const isEditable = record.isEditAllowed;
        const hasComplements = record.complement;

        if (!isEditable && !hasComplements) {
          return <span>Sin complemento asociado</span>;
        }
        const complementState = isEditable
          ? hasComplements
            ? "edit"
            : "add"
          : "view";

        const complementConfig = {
          add: {
            label: "Agregar información para complemento",
            icon: <PlusOutlined />,
          },
          edit: {
            label: "Editar información de complemento",
            icon: <EditOutlined />,
          },
          view: {
            label: "Ver información de complemento",
            icon: <EyeOutlined />,
          },
        };
        const currentComplement = complementConfig[complementState];

        return (
          <Space>
            <Link
              key="toComplement"
              to={{
                pathname: `/complement/${record.id}/${COMPLEMENT_PARENT.RECEPCION}/${distributionPoint?.id}`,
                state: {
                  from: location.pathname + location.search,
                },
              }}
            >
              <Popover content={currentComplement.label}>
                <Button icon={currentComplement.icon} />
              </Popover>
            </Link>
            {hasComplements && (
              <Popover content="Eliminar información de complemento">
                <Button
                  icon={<DeleteOutlined />}
                  danger
                  onClick={() => {
                    deleteComplement(record);
                  }}
                />
              </Popover>
            )}
          </Space>
        );
      },
    },
  ];

  const columnsInside: TableColumnsType<ResponseInputRegisterMovements> = [
    {
      title: "Volumen",
      dataIndex: "rmVolume",
      width: "20%",
      render: (text) => `${text}`.replace(/\B(?=(\d{3})+(?!\d))/g, ","),
    },
    {
      title: "Masa",
      dataIndex: "rmMass",
      width: "20%",
      render: (text) => `${text}`.replace(/\B(?=(\d{3})+(?!\d))/g, ","),
    },
    {
      title: "Folio del Movimiento",
      dataIndex: "rmFolio",
      width: "20%",
    },
    {
      title: "Fecha de inicio del movimiento",
      dataIndex: "rmStartDate",
      width: "20%",
      render: (date) => moment(date).format("YYYY/MM/DD HH:mm:ss"),
    },
    {
      title: "Fecha de fin del movimiento",
      dataIndex: "rmEndDate",
      width: "20%",
      render: (date) => moment(date).format("YYYY/MM/DD HH:mm:ss"),
    },
  ];

  function handleTableChange(
    _pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter:
      | SorterResult<ResponseEntryDetail>
      | SorterResult<ResponseEntryDetail>[]
  ) {
    // Manejar filtros
    const fiscalIdFilter = filters.fiscalId;
    if (fiscalIdFilter && fiscalIdFilter.length === 1) {
      const filterCfdi = fiscalIdFilter[0] as boolean;
      setFilterCfdi(filterCfdi);
    } else {
      setFilterCfdi(undefined);
    }

    // Manejar ordenamiento
    if (!Array.isArray(sorter) && sorter.field && sorter.order) {
      const { field, order } = sorter;
      const direction = order === "ascend" ? "ASC" : "DESC";

      // Mapeo de campos de ordenamiento
      const sortMapping: Record<string, string> = {
        inputRegisterReceptionDate: "initialDate",
        inputRegisterEndReceptionDate: "endDate",
        inputRegisterFolio: "folio",
        invoiceSerie: "invoiceSerie",
        invoiceFolio: "invoiceFolio",
      };

      const sortField = sortMapping[field as string] || "endDate";

      pagination.setSort([`${sortField},${direction}`]);
    } else {
      // Reset al ordenamiento por defecto si no hay ordenamiento activo
      pagination.setSort(["endDate,DESC"]);
    }
  }

  return (
    <Table
      title={() => (
        <span style={{ marginLeft: "10px" }}>
          Presione <PlusSquareOutlined /> para ver movimientos de la recepción
        </span>
      )}
      rowSelection={selection.rowSelection}
      rowKey="id"
      style={{ width: "100%" }}
      columns={columns}
      dataSource={receptions}
      expandable={{
        expandedRowRender: (r) => (
          <Table
            className="nested-table"
            columns={columnsInside}
            dataSource={r.inputRegisterMovements}
            pagination={false}
            rowKey="rmFolio"
          />
        ),
      }}
      loading={isLoading}
      pagination={{
        ...pagination.paginationConfig,
        onChange: pagination.onPageChange,
        onShowSizeChange: pagination.onPageSizeChange,
      }}
      onChange={handleTableChange}
      scroll={{
        x: 1500,
        y: "calc(100vh - 600px)",
      }}
    />
  );
}

export default ReceptionTable;
